// The create screen's decisions about a draft's life (pages/tickets/New.jsx): what filing
// sends and how a refusal lands back on the form, what a save state says on screen, what a
// replayed buffer's answer means, which stale choices block filing, and where the agent goes
// after a discard. Pure, for node --test (test/web/draft-flow.test.js).
//
// docs/superpowers/specs/2026-10-13-multi-ticket-drafts-design.md ("Filing from a tab",
// "Saving, syncing, and failure", "Reopening an old draft"); docs/API.md §12.

import { childTouched, omitUntouched } from './draft-model.js';
import { findStaleChoices } from './stale-config.js';
import { toTabs } from './tab-model.js';
import { draftHref } from './draft-address.js';
import { supportPath } from '../../support-path.js';

/**
 * The caller of the call. A child's filing body leaves these out: the server copies them
 * from the parent whatever the child says (docs/API.md §12.5).
 */
export const INHERITED_CALLER_FIELDS = [
  'customer_id', 'organization_id', 'location_id', 'cc_customer_ids', 'follower_user_ids', 'channel',
];

/** A child's `POST /api/tickets` body (child-tickets.js `childBody`) as filing takes it. */
export function childFileBody(body) {
  const out = { ...body };
  for (const key of INHERITED_CALLER_FIELDS) delete out[key];
  // Its parent is the ticket this filing creates; the server drops the field anyway.
  delete out.parent_ticket_id;
  return out;
}

/**
 * The `POST /api/ticket-drafts/:id/file` body. `parent` is the main ticket's create body
 * (New.jsx `buildBody`) with the untouched form, queue and owner left OUT, so they resolve
 * now, at filing (an absent owner is the filer; after a take-over, the lead). `children`
 * are `{ body, touched }`: a child-tickets.js `childBody` result and that child's own
 * `touched` (draft-model.js `childTouched`), whose untouched form, queue and owner are left
 * out the same way. `links` are the screen's `{ public_ref }` rows.
 */
export function fileRequest({ revision, parent, touched, children = [], links = [] }) {
  return {
    revision,
    parent: omitUntouched(parent, touched),
    // A missing `touched` counts as all touched (childTouched), never as none.
    children: children.map((child) => childFileBody(omitUntouched(child.body, childTouched(child)))),
    links: links.map((link) => String(link.public_ref)),
  };
}

/** A field path the server names (`custom_fields.pos`) as the key the form shows it under (`custom.pos`). */
function formKey(field) {
  if (field.startsWith('custom_fields.')) return `custom.${field.slice('custom_fields.'.length)}`;
  if (field === 'initial_message' || field.startsWith('initial_message.')) return 'message';
  if (field === 'customer_id') return 'customer';
  return field;
}

/**
 * A refused filing as the form's errors and a banner. One `422 VALIDATION_FAILED` carries
 * every problem at its path (`subject`, `children[1].priority`, `links[0]`); those are keyed
 * the way the screen keys its own checks (`custom.<key>`, `child.<childKey>.<field>`), with
 * "Child ticket N: " in front of a child's, which ChildTicketCard's errors strip. Anything
 * else, the store rules and NESTING_TOO_DEEP included, arrives alone and is the banner.
 *
 * @param {{ children?: { key: string }[], links?: { public_ref: string }[] }} form
 */
export function mapFilingError(error, { children = [], links = [] } = {}) {
  const entries = error?.code === 'VALIDATION_FAILED' ? error.details?.fields : null;
  if (!Array.isArray(entries)) {
    return { errors: {}, banner: error?.message || 'Something went wrong.' };
  }
  const errors = {};
  const put = (key, message) => {
    if (!(key in errors)) errors[key] = message;
  };
  for (const { field = '', message } of entries) {
    const child = /^children\[(\d+)\](?:\.(.+))?$/.exec(field);
    const link = /^links\[(\d+)\]$/.exec(field);
    if (child && children[Number(child[1])]) {
      const index = Number(child[1]);
      put(`child.${children[index].key}.${formKey(child[2] ?? 'ticket')}`, `Child ticket ${index + 1}: ${message}`);
    } else if (link && links[Number(link[1])]) {
      const index = Number(link[1]);
      put(`link.${index}`, `Related ticket #${links[index].public_ref}: ${message}`);
    } else if (child || link) {
      put(field, message);
    } else {
      put(formKey(field), message);
    }
  }
  return { errors, banner: 'Nothing was filed. Fix the problems below and create again.' };
}

/**
 * What the answer to REPLAYING a buffered save on load means (draft-buffer.js):
 *   conflict  DRAFT_STALE: someone saved since; the server's copy wins, the text is shown once.
 *   taken     a lead took the draft over; the text is shown once.
 *   closed    filed or discarded; the buffer has nothing left to do.
 *   gone      404: not this person's draft (any more).
 *   keep      anything else (offline, a 5xx, a refusal such as DRAFT_LIMIT): keep the buffer,
 *             show its text in the form, and let the save controller try again.
 */
export function replayOutcome(error) {
  switch (error?.code) {
    case 'DRAFT_STALE': return 'conflict';
    case 'DRAFT_TAKEN_OVER': return 'taken';
    case 'DRAFT_FILED':
    case 'DRAFT_DISCARDED': return 'closed';
    default: return error?.status === 404 ? 'gone' : 'keep';
  }
}

/**
 * What a refused replay shows ONCE (`{ heading, text }`), or null for nothing: `text` is
 * the buffered edit's `unsavedText`. A conflict always says the draft was changed on
 * another PC; the other outcomes only when something was typed; a draft already filed or
 * discarded has nothing to show (the ticket holds the content, or the call was abandoned).
 */
export function replayNotice(outcome, text) {
  if (outcome === 'conflict') {
    return {
      heading: 'This draft was changed on another PC, so it shows the saved version. What was typed here could not be saved:',
      text,
    };
  }
  if (outcome === 'closed' || !text) return null;
  return { heading: 'What was typed here could not be saved:', text };
}

/**
 * What the create screen does with its save controller as it unmounts: `flush` what is
 * unsaved (dirty, saving, or failed but retryable here), or `drop` it. Nothing is flushed
 * once the draft was filed or discarded from this screen (`finished`), after a sign-out
 * (`ended`), or for a draft the server answered 404 to (not this person's to save).
 */
export function unmountPlan(snapshot, { finished, ended }) {
  if (finished || ended || !snapshot) return 'drop';
  if (snapshot.state === 'failed') return snapshot.error?.status === 404 ? 'drop' : 'flush';
  return snapshot.state === 'dirty' || snapshot.state === 'saving' ? 'flush' : 'drop';
}

/**
 * What a save state (draft-sync.js snapshot) says on screen, or null for nothing to say.
 * `readOnly` is true once the draft can no longer be saved from here.
 * @returns {{ kind: string, tone: 'info'|'warn'|'problem', message: string, readOnly: boolean, publicRef?: string } | null}
 */
export function syncNotice(snapshot) {
  switch (snapshot?.state) {
    case 'failed':
      if (snapshot.error?.status === 404) {
        return { kind: 'gone', tone: 'problem', readOnly: true, message: 'This draft is no longer available to you, so it cannot be saved.' };
      }
      if (snapshot.transient) {
        return {
          kind: 'offline', tone: 'warn', readOnly: false,
          message: 'Not saved yet: the server could not be reached. Your changes are kept in this browser, and saving is retried.',
        };
      }
      return { kind: 'refused', tone: 'problem', readOnly: false, message: snapshot.error?.message || 'The draft could not be saved.' };
    case 'stale':
      return { kind: 'stale', tone: 'warn', readOnly: false, message: 'This draft was changed on another PC.' };
    case 'taken':
      return { kind: 'taken', tone: 'warn', readOnly: true, message: `${snapshot.by || 'A lead'} took over this draft.` };
    case 'filed':
      return { kind: 'filed', tone: 'info', readOnly: true, message: `Filed as ticket #${snapshot.public_ref}.`, publicRef: snapshot.public_ref };
    case 'discarded':
      return { kind: 'discarded', tone: 'info', readOnly: true, message: 'This draft was discarded.' };
    default:
      return null;
  }
}

/** A 409 about a closed draft (on load, discard or filing) as the notice its save state would give; else null. */
export function noticeForError(error) {
  switch (error?.code) {
    case 'DRAFT_FILED': return syncNotice({ state: 'filed', public_ref: error.details?.public_ref });
    case 'DRAFT_DISCARDED': return syncNotice({ state: 'discarded' });
    case 'DRAFT_TAKEN_OVER': return syncNotice({ state: 'taken', by: error.details?.by });
    default: return null;
  }
}

/**
 * The snapshot to report to the tab row (DraftsProvider `reportStatus`): a save answered
 * 404 is a draft that is not this person's, so it is forgotten (null) rather than marked.
 */
export function tabStatus(snapshot) {
  if (snapshot?.state === 'failed' && snapshot.error?.status === 404) return null;
  return snapshot;
}

const LIST_OF = { form_id: 'forms', queue_id: 'queues', assigned_user_id: 'assignees' };

/**
 * stale-config.js's findings for the screen, with the draft's store row (`{ id, is_active }`,
 * fetched by the screen) as `config.location`. A finding that rests on a list which FAILED
 * to load is dropped: an empty list because the request failed says nothing was deleted.
 */
export function staleChoicesFor(screen, touched, config, storeRow) {
  const failed = new Set(config?.failed ?? []);
  return findStaleChoices(screen, touched, { ...config, location: storeRow ?? null }).filter((finding) => {
    const list = finding.field.startsWith('custom_fields.') ? 'forms' : LIST_OF[finding.field];
    return !list || !failed.has(list);
  });
}

/**
 * The heading over blocking stale findings. "Started before the setup changed" is a claim
 * about when the draft was made, so it is made only for a draft that already existed on the
 * server when the screen opened; a draft begun in this sitting says what to do.
 */
export function staleHeading({ wasSaved } = {}) {
  return wasSaved
    ? 'This draft was started before the setup changed. Resolve these before creating:'
    : 'Fix these before creating:';
}

/** Why Create must wait, or null: the blocking findings' messages. */
export function blockingReason(findings) {
  const blocking = (findings ?? []).filter((finding) => finding.blocking);
  if (!blocking.length) return null;
  return `Resolve before creating: ${blocking.map((finding) => finding.message).join(' ')}`;
}

/**
 * Where to go once draft `id` is closed: the tab to its right, else the one to its left
 * (the order of the tab row, tab-model.js), else the queue.
 */
export function nextDraftHref(drafts, id, now = Date.now()) {
  const tabs = toTabs(drafts ?? [], now);
  const others = tabs.filter((tab) => tab.id !== id);
  if (!others.length) return supportPath('/tickets');
  const index = tabs.findIndex((tab) => tab.id === id);
  if (index === -1) return others[0].href;
  return (tabs[index + 1] ?? tabs[index - 1]).href;
}

/** Whether `pathname` is draft `id`'s own address (GUIDs compared case-blind). */
export function isDraftAddress(pathname, id) {
  return String(pathname ?? '').replace(/\/+$/, '').toLowerCase() === draftHref(id).toLowerCase();
}

/** The quiet save status beside Create: '' until something has been saved or is being saved. */
export function saveStatusLabel(snapshot) {
  switch (snapshot?.state) {
    case 'idle': return snapshot.revision > 0 ? 'Saved' : '';
    case 'dirty':
    case 'saving': return 'Saving…';
    case 'failed': return 'Not saved';
    default: return '';
  }
}
